import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { PublicCheckRequest } from '@shared/types';
import { submitPublicCheck } from '../publicApi';
import { useStructuredData } from '../useStructuredData';

// Cloudflare Turnstile。未設定時は公式の「常にpass」テスト用サイトキーを使う。
const TURNSTILE_SITE_KEY =
  import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '1x00000000000000000000AA';
const TURNSTILE_SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

// Turnstile のグローバルAPI（explicit renderのみ利用）
interface TurnstileAPI {
  render: (
    el: HTMLElement,
    opts: {
      sitekey: string;
      callback: (token: string) => void;
      'error-callback'?: () => void;
      'expired-callback'?: () => void;
    },
  ) => string;
  reset: (widgetId?: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileAPI;
  }
}

function isValidUrl(value: string): boolean {
  try {
    const u = new URL(value);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

export default function PublicLandingPage() {
  const navigate = useNavigate();
  useStructuredData();

  const [url, setUrl] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [token, setToken] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const widgetRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);

  // Turnstile スクリプトをこのページ内で動的ロードし、explicit render する。
  useEffect(() => {
    let cancelled = false;

    function renderWidget() {
      if (cancelled) return;
      if (!window.turnstile || !widgetRef.current || widgetIdRef.current) return;
      widgetIdRef.current = window.turnstile.render(widgetRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (t: string) => setToken(t),
        'error-callback': () => setToken(''),
        'expired-callback': () => setToken(''),
      });
    }

    if (window.turnstile) {
      renderWidget();
      return () => {
        cancelled = true;
      };
    }

    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${TURNSTILE_SCRIPT_SRC}"]`,
    );
    const script = existing ?? document.createElement('script');
    if (!existing) {
      script.src = TURNSTILE_SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
    const onLoad = () => renderWidget();
    script.addEventListener('load', onLoad);
    // 既にロード済みだった場合の保険
    if (window.turnstile) renderWidget();

    return () => {
      cancelled = true;
      script.removeEventListener('load', onLoad);
    };
  }, []);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!isValidUrl(url.trim())) {
      setError('URLの形式が正しくありません。https:// から始まるURLを入力してください。');
      return;
    }
    if (!isValidEmail(email.trim())) {
      setError('メールアドレスの形式が正しくありません。');
      return;
    }
    if (!company.trim()) {
      setError('会社名もしくはお名前を入力してください。');
      return;
    }
    if (!token) {
      setError('認証（Turnstile）が完了していません。少し待ってから再度お試しください。');
      return;
    }

    setSubmitting(true);
    try {
      const payload: PublicCheckRequest = {
        url: url.trim(),
        email: email.trim(),
        company: company.trim(),
        phone: phone.trim() || undefined,
        turnstileToken: token,
        source: window.location.search ? window.location.search.slice(1) : undefined,
      };
      const res = await submitPublicCheck(payload);
      navigate(`/result/${res.checkId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      // 使い捨てトークンなので再送信のためリセット
      setToken('');
      if (window.turnstile && widgetIdRef.current) {
        window.turnstile.reset(widgetIdRef.current);
      }
      setSubmitting(false);
    }
  }

  return (
    <div className="public-lp">
      <section className="public-hero">
        <div className="public-hero__inner">
          <span className="public-hero__badge">無料・登録不要</span>
          <h1 className="public-hero__title">
            あなたのサイト、
            <br />
            <strong>Google公式基準</strong>で無料SEO診断
          </h1>
          <p className="public-hero__lead">
            Google Search Central 公式ドキュメントに基づく独自ルーブリックで、
            テクニカル基盤・ページ体験(Core Web Vitals)・コンテンツ品質・構造化データを
            総合スコアで可視化。改善すべき課題がその場で分かります。
          </p>
          <ul className="public-hero__points">
            <li>Google公式SEO基準ベースの診断</li>
            <li>総合スコア＋カテゴリ別レーダーで一目で把握</li>
            <li>優先度の高い改善課題を提示</li>
          </ul>
        </div>

        <form className="public-form card" onSubmit={onSubmit}>
          <h2 className="public-form__title">無料で診断する</h2>
          <p className="public-form__note muted small">
            メールアドレスをご登録いただくと、その場で診断結果をご覧いただけます。
          </p>

          <label className="public-field">
            <span className="public-field__label">
              診断するURL<span className="public-field__req">必須</span>
            </span>
            <input
              type="url"
              inputMode="url"
              placeholder="https://example.com/"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              disabled={submitting}
              required
            />
          </label>

          <label className="public-field">
            <span className="public-field__label">
              メールアドレス<span className="public-field__req">必須</span>
            </span>
            <input
              type="email"
              inputMode="email"
              placeholder="you@company.co.jp"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
              required
            />
          </label>

          <label className="public-field">
            <span className="public-field__label">
              会社名もしくはお名前<span className="public-field__req">必須</span>
            </span>
            <input
              type="text"
              placeholder="株式会社サンプル / 山田太郎"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              disabled={submitting}
              required
            />
          </label>

          <label className="public-field">
            <span className="public-field__label">
              電話番号<span className="public-field__opt">任意</span>
            </span>
            <input
              type="tel"
              inputMode="tel"
              placeholder="03-0000-0000"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              disabled={submitting}
            />
          </label>

          <div className="public-turnstile" ref={widgetRef} />

          {error && <div className="alert alert--error">{error}</div>}

          <button
            type="submit"
            className="btn btn--primary btn--lg public-form__cta"
            disabled={submitting}
          >
            {submitting ? (
              <>
                <span className="spinner spinner--sm" />
                診断を実行中…
              </>
            ) : (
              '無料でSEO診断する'
            )}
          </button>

          <p className="public-form__privacy muted small">
            ご入力いただいた情報は、診断結果のご提供およびご連絡の目的でのみ利用します。
          </p>
        </form>
      </section>

      <p className="public-lp__disclaimer">
        ※ 本診断のスコアは、Google公式ガイド（社内ナレッジ版）を基にした独自基準のルーブリック採点です。
        Googleの実際の評価・検索順位そのものを示すものではありません。
      </p>
    </div>
  );
}
